/**
 * The ticker catalogue. Instruments are shared across users — a search hits the
 * local catalogue only (no provider call, `LIMIT 20`, no pagination), while
 * `POST /instruments` resolves-or-creates and is the only endpoint that can
 * spend a third-party price call.
 */
export const ASSET_TYPES = [
  "STOCK",
  "ETF",
  "CRYPTO",
  "FOREX",
  "OTHER",
] as const;

export type AssetType = (typeof ASSET_TYPES)[number];

/** The instrument shape embedded in every client-facing payload. */
export interface InstrumentSummary {
  id: number;
  symbol: string;
  exchange: string;
  name: string;
  assetType: AssetType;
  nativeCurrency: string;
  /** ISO 3166-1 alpha-2 country the dividends are declared under; null when not set. */
  sourceCountry: string | null;
}

export interface InstrumentPrice {
  instrumentId: number;
  date: string;
  priceNative: number;
  fxToEur: number;
  priceEur: number;
  source: string;
}

/**
 * A catalogue row from `GET /instruments?search=`. `latestPrice` is `null` when the instrument
 * has never been priced — the signal to warn before a buy is submitted, since
 * the server rejects a trade on an instrument with no price on or before the
 * trade date.
 */
export interface CatalogueSearchResult extends InstrumentSummary {
  isActive: boolean;
  latestPrice: InstrumentPrice | null;
}

/** A curated popular listing not yet in the catalogue: no `id`, no price, nothing to trade on until resolved. */
interface PopularListingFields {
  symbol: string;
  exchange: string;
  name: string;
}

export interface PopularMarketListingSuggestion extends PopularListingFields {
  assetType: Extract<AssetType, "STOCK" | "ETF">;
}

export interface PopularPairListingSuggestion extends PopularListingFields {
  assetType: Extract<AssetType, "CRYPTO" | "FOREX">;
  nativeCurrency: string;
}

export type PopularListingSuggestion =
  PopularMarketListingSuggestion | PopularPairListingSuggestion;

export type InstrumentSearchResult =
  CatalogueSearchResult | PopularListingSuggestion;

export const isCatalogueSearchResult = (
  result: InstrumentSearchResult,
): result is CatalogueSearchResult => "id" in result;

/**
 * A `POST /instruments` row. Deliberately *not* the same type as
 * `CatalogueSearchResult`: this shape is flat, carries `created`/`priced` and
 * has no `latestPrice` at all. Conflating the two would make a newly created,
 * successfully priced ticker render as unpriced.
 */
export interface ResolvedInstrument extends InstrumentSummary {
  isActive: boolean;
  yahooSymbol: string | null;
  created: boolean;
  priced: boolean;
}

export interface ResolveInstrumentPayload {
  symbol: string;
  exchange: string;
  assetType: AssetType;
  name?: string;
  /** Required by the server for CRYPTO/FOREX when the price probe can't derive it. */
  nativeCurrency?: string;
}

/** A private historical price supplied by the current user for one instrument. */
export interface PriceOverride {
  instrumentId: number;
  date: string;
  priceNative: number;
  fxToEur: number;
  priceEur: number;
  priceProvenance: "userOverride";
}

/** The write response adds whether a row for this date was replaced. */
export interface RecordedPriceOverride extends PriceOverride {
  replacedExisting: boolean;
}

export interface RecordPriceOverridePayload {
  instrumentId: number;
  date: string;
  priceNative: number;
  /** Omit for EUR-denominated instruments; the API infers its fixed 1:1 rate. */
  fxToEur?: number;
}

export type ListingDiscoveryOutcome = "MATCH" | "AMBIGUOUS" | "NO_MATCH";

/**
 * One candidate from `GET /instruments/listings`. `currency` is deliberately absent — discovery
 * never states one, and the create takes it from the confirmed chart instead.
 */
export interface DiscoveredListing {
  yahooSymbol: string;
  name: string | null;
  providerType: string | null;
  providerExchange: string | null;
  exchangeDisplayName: string | null;
  assetType: AssetType | null;
}

export interface DiscoveredListings {
  outcome: ListingDiscoveryOutcome;
  candidates: DiscoveredListing[];
}

export interface DiscoverListingsPayload {
  symbol: string;
  exchange: string;
}

/** `yahooSymbol` must be one that discovery named — the server never picks a listing implicitly. */
export interface CreateListedInstrumentPayload {
  symbol: string;
  exchange: string;
  yahooSymbol: string;
}

export interface SetInstrumentSourceCountryPayload {
  instrumentId: number;
  sourceCountry: string | null;
}

// A bare instrument id is how a manual-only pair returns to automatic pricing.
export type RepairInstrumentListingPayload = { instrumentId: number } & (
  | { yahooSymbol: string }
  | { manualPricingOnly: true }
  | { yahooSymbol?: never; manualPricingOnly?: never }
);

/** Shared with every holder, unlike a private override; omit `fxToEur` to let the API resolve the rate. */
export interface RecordInstrumentPricePayload {
  instrumentId: number;
  date: string;
  priceNative: number;
  fxToEur?: number;
}
